import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Trophy, Medal, ChevronDown, ChevronLeft, ChevronRight, Sparkles, Flame, CalendarCheck, Radio, Award, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { currentMonthISO } from "@/lib/dateFormat";

const RANK_STYLE = {
  1: "bg-yellow-400 text-yellow-950",
  2: "bg-slate-300 text-slate-800",
  3: "bg-amber-600 text-white",
};

const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const shiftMonth = (key, delta) => {
  const [y, m] = key.split("-").map(Number);
  return monthKey(new Date(y, m - 1 + delta, 1));
};

export default function LeaderboardCard({ preview = 3 }) {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [month, setMonth] = useState(currentMonthISO());
  const [rows, setRows] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [help, setHelp] = useState(false);

  useEffect(() => {
    setRows(null);
    api.get("/gamification/leaderboard", { params: { limit: 500, month } }).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [month]);

  const monthLabel = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const s = new Date(y, m - 1, 1).toLocaleDateString(lang === "en" ? "en-US" : "es-CR", { month: "long", year: "numeric" }); // tz-ok: mes armado con números, no con un instante
    return s.charAt(0).toUpperCase() + s.slice(1);
  }, [month, lang]);

  const visible = rows ? (expanded ? rows : rows.slice(0, preview)) : [];
  const hidden = rows ? Math.max(0, rows.length - preview) : 0;
  const isCurrent = month === currentMonthISO();

  return (
    <Card className="p-5" data-testid="leaderboard-card">
      <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Trophy className="h-4 w-4 text-yellow-500" /> {t("leaderboard_title")}
        </h3>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setHelp(true)}
            className="badge-how-btn mr-1 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold text-white shadow-md transition-transform hover:scale-105 active:scale-95"
            data-testid="leaderboard-help-toggle">
            <Sparkles className="h-3.5 w-3.5" /> {t("badges_how_btn")}
          </button>
          <button type="button" onClick={() => setMonth((m) => shiftMonth(m, -1))} className="h-7 w-7 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="leaderboard-prev-month" aria-label="prev">
            <ChevronLeft className="h-4 w-4 text-slate-500" />
          </button>
          <span className="text-sm font-medium text-slate-700 min-w-[130px] text-center" data-testid="leaderboard-month">{monthLabel}</span>
          <button type="button" onClick={() => setMonth((m) => shiftMonth(m, 1))} disabled={isCurrent}
            className="h-7 w-7 rounded-full hover:bg-slate-100 flex items-center justify-center disabled:opacity-30" data-testid="leaderboard-next-month" aria-label="next">
            <ChevronRight className="h-4 w-4 text-slate-500" />
          </button>
        </div>
      </div>
      {user?.role === "student" && (
        <div className="flex justify-end mb-2 text-xs">
          <Link to="/dashboard/achievements" className="font-semibold text-brand-blue hover:underline" data-testid="leaderboard-my-badges-link">
            {t("my_achievements")} →
          </Link>
        </div>
      )}

      <Dialog open={help} onOpenChange={setHelp}>
        <DialogContent className="max-w-lg p-0 overflow-hidden border-0 bg-transparent shadow-none [&>button]:hidden" data-testid="leaderboard-help">
          <div className="badge-how-panel relative rounded-3xl p-6 text-white">
            <button type="button" onClick={() => setHelp(false)} className="absolute right-4 top-4 h-8 w-8 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center" data-testid="leaderboard-help-close" aria-label="close">
              <X className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-2xl bg-white/15 flex items-center justify-center badge-pop"><Trophy className="h-6 w-6 text-yellow-300" /></div>
              <div>
                <DialogTitle className="font-display text-xl font-bold text-white">{t("badges_how_title")}</DialogTitle>
                <p className="text-xs text-white/80">{t("badges_how_intro")}</p>
              </div>
            </div>
            <ul className="mt-5 space-y-2.5">
              {[
                { Icon: Flame, k: "badges_how_1", c: "bg-orange-400" },
                { Icon: CalendarCheck, k: "badges_how_2", c: "bg-emerald-400" },
                { Icon: Radio, k: "badges_how_3", c: "bg-sky-400" },
                { Icon: Award, k: "badges_how_4", c: "bg-fuchsia-400" },
              ].map(({ Icon, k, c }) => (
                <li key={k} className="flex gap-3 rounded-2xl bg-white/10 backdrop-blur px-3 py-2.5 text-sm leading-snug">
                  <span className={`h-8 w-8 shrink-0 rounded-xl ${c} text-slate-900 flex items-center justify-center`}><Icon className="h-4 w-4" /></span>
                  <span>{t(k)}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs text-white/75">{t("badges_how_note")}</p>
            {user?.role === "student" && (
              <Link to="/dashboard/achievements" onClick={() => setHelp(false)}
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-yellow-400 px-5 py-2.5 text-sm font-bold text-yellow-950 hover:bg-yellow-300 transition-colors"
                data-testid="leaderboard-help-achievements">
                <Medal className="h-4 w-4" /> {t("my_achievements")}
              </Link>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {rows === null ? (
        <div className="text-sm text-slate-400">…</div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="leaderboard-empty">{t("leaderboard_empty_month")}</p>
      ) : (
        <>
          <ol className="divide-y divide-slate-100">
            {visible.map((r) => (
              <li key={r.user_id} className={`flex items-center gap-3 py-2 ${r.user_id === user?.user_id ? "bg-brand-blue/5 -mx-2 px-2 rounded-md" : ""}`}
                data-testid={`leaderboard-row-${r.rank}`}>
                <span className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${RANK_STYLE[r.rank] || "bg-slate-100 text-slate-600"}`}>
                  {r.rank}
                </span>
                <span className="flex-1 text-sm font-medium text-slate-900 truncate">{r.name}</span>
                <span className="flex items-center gap-1 text-xs text-slate-500"><Medal className="h-3.5 w-3.5" />{r.badges}</span>
                <span className="font-display font-bold text-slate-900 w-16 text-right">{r.points} <span className="text-[10px] font-sans font-normal text-slate-500">{t("points_short")}</span></span>
              </li>
            ))}
          </ol>
          {hidden > 0 && (
            <button type="button" onClick={() => setExpanded((e) => !e)}
              className="mt-3 w-full inline-flex items-center justify-center gap-1 text-xs font-semibold text-brand-blue hover:underline"
              data-testid="leaderboard-expand">
              {expanded ? t("leaderboard_show_less") : t("leaderboard_show_more", { n: hidden })}
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
            </button>
          )}
        </>
      )}
    </Card>
  );
}
